/**
 * End-of-run Chronicle and summary.
 *
 * The headline KPI is Second Adventure Start Rate (GDD §15), so this screen's
 * job is not to congratulate — it is to make the player feel their run was a
 * story with a shape, and to put "다시 굴린다" one tap away.
 */

import type { GameSession } from '@lastroll/engine';

export interface ChronicleProps {
  session: GameSession;
  onRestart: () => void;
}

export function Chronicle({ session, onRestart }: ChronicleProps): JSX.Element {
  const chapters = session.chronicle();
  const s = session.summary();
  const ended = session.state.ended;
  const maxFace = Math.max(1, ...s.faceDistribution);

  const cells: [string, string | number][] = [
    ['굴린 횟수', s.rolls],
    ['운명 조작', s.manipulations],
    ['만난 사람', s.npcsMet],
    ['구한 사람', s.npcsSaved],
    ['죽인 사람', s.npcsKilled],
    ['쓰러뜨림', s.monstersKilled],
    ['살려 보냄', s.monstersSpared],
    ['발견한 비밀', s.secrets],
    ['이스터에그', s.easterEggs],
    ['남은 운명 빚', s.fateDebt],
  ];

  return (
    <div className="wrap" style={{ paddingBottom: 32 }}>
      <div className="panel">
        <h2 className="panel__title">
          {ended?.reason === 'victory'
            ? '모험이 끝났다'
            : ended?.reason === 'death'
              ? '여기서 한 번 끝났다'
              : '모험을 중단했다'}
        </h2>
        <p className="panel__body">
          {ended?.reason === 'victory'
            ? '세계는 조금 달라졌다. 그리고 그것을 만든 것은 네 굴림이다.'
            : '이야기는 끝나지 않았다. 세계는 이것도 기억한다.'}
        </p>
      </div>

      <div className="section-title">연대기</div>
      {chapters.length === 0 ? (
        <p className="muted">아직 기록될 만한 일이 일어나지 않았다.</p>
      ) : (
        chapters.map((c) => (
          <div key={c.chapter} className="chapter">
            <div className="chapter__no">Chapter {c.chapter}</div>
            <h3 className="chapter__title">{c.title}</h3>
            {c.lines.map((line, i) => (
              <p key={i} className="chapter__line">
                {line}
              </p>
            ))}
          </div>
        ))
      )}

      {s.definingRoll && (
        <div className="panel">
          <h3 className="panel__title" style={{ fontSize: 17 }}>
            이번 모험을 결정한 굴림
          </h3>
          <p className="panel__body">
            {s.definingRoll.seq}번째 굴림. 그 한 번이 {s.definingRoll.causedEvents}가지 결과를
            만들었다.
          </p>
        </div>
      )}

      <div className="section-title">숫자</div>
      <div className="summary">
        {cells.map(([label, value]) => (
          <div key={label} className="summary__cell">
            <div className="summary__label">{label}</div>
            <div className="summary__value">{value}</div>
          </div>
        ))}
      </div>

      <div className="section-title">눈 분포</div>
      <div className="faces" aria-label="주사위 눈 분포">
        {s.faceDistribution.map((count, i) => (
          <div
            key={i}
            className="faces__bar"
            style={{ height: `${(count / maxFace) * 100}%` }}
            title={`${i + 1}: ${count}회`}
          >
            <span>{i + 1}</span>
          </div>
        ))}
      </div>

      {s.titles.length > 0 && (
        <>
          <div className="section-title" style={{ marginTop: 40 }}>
            얻은 칭호
          </div>
          <p className="panel__body">{s.titles.join(' · ')}</p>
        </>
      )}
      {s.dominantTrait && (
        <p className="muted" style={{ marginTop: 8 }}>
          가장 두드러진 성향: {s.dominantTrait}
        </p>
      )}

      <div className="actionbar" style={{ position: 'static', background: 'none', paddingLeft: 0, paddingRight: 0 }}>
        <button type="button" className="roll-btn" onClick={onRestart}>
          다시 굴린다
        </button>
      </div>
    </div>
  );
}
